import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, List } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import MatchDetailPanel from './MatchDetailPanel';

/**
 * Full-page inline match detail view — same layout FullscreenScheduleOverlay
 * uses for its own Previous/Next (title/back row, then a centered-label
 * Previous/Next bar, then MatchDetailPanel's content), not a centered dialog
 * with a backdrop. A small floating modal read as an odd, cramped detour
 * when the surrounding schedule table already fills the page — this instead
 * takes over the same way fullscreen's own detail view does. Opened by
 * clicking a match row (see MatchRow.jsx's row-level onClick) and closed by
 * the organizer without leaving the schedule they were looking at — this
 * view owns no schedule state of its own, so closing it never loses the
 * current view/filters/scroll position underneath.
 *
 * `rows`/`index` — the CURRENT list's own already-filtered, already-ordered
 * row set and which position within it is open (see MatchTable.jsx, which
 * owns this state) — Previous/Next step through THAT list, not some
 * separate global concept of "every match in the tournament." A list is
 * already scoped to whatever's meaningful for it (one group's matches, one
 * ladder court's matches, the merged full schedule, ...), and whatever
 * Filters panel selections are active already narrowed it down — so
 * Previous/Next naturally follows "the schedule for this court/group" with
 * no extra logic here.
 */
const MatchDetailModal = ({ isOpen, onClose, rows, index, onPrev, onNext, tournamentVO, currentUserId, startScoring, openLiveGame }) => {
  const { theme } = useTheme();

  if (!isOpen || index == null || !rows?.[index]) return null;

  const row = rows[index];
  const hasPrev = index > 0;
  const hasNext = index < rows.length - 1;
  const currentLabel = `${row.liveData?.participant_a_name || row.slotA?.name || row.placeholderA || 'Team A'} vs ${row.liveData?.participant_b_name || row.slotB?.name || row.placeholderB || 'Team B'}`;

  const handleStartScoring = (matchRow) => {
    onClose();
    startScoring?.(matchRow);
  };

  const handleOpenLive = (matchRow) => {
    onClose();
    openLiveGame?.(matchRow);
  };

  return createPortal(
    // z-[55]: same layer FullscreenScheduleOverlay uses — above
    // MobileNavFooter.jsx's fixed bottom nav (z-50, mobile-only), so this
    // full-page takeover actually covers the site chrome. The two never
    // nest inside each other (this opens from the plain schedule table,
    // fullscreen has its own separate Previous/Next for the same content),
    // so there's no need to layer above/below FullscreenScheduleOverlay itself.
    <div className={`fixed inset-0 z-[55] flex flex-col ${theme.background}`}>
      <div
        className={`flex shrink-0 items-center justify-between gap-3 border-b ${theme.border} px-4 pb-3`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <div className={`min-w-0 truncate text-sm font-bold ${theme.text}`}>Match Details</div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Back to List"
          className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
        >
          <List size={13} />
          <span className="hidden sm:inline">Back to List</span>
        </button>
      </div>

      <div className={`flex shrink-0 items-center gap-2 border-b ${theme.border} px-3 py-2`}>
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          aria-label="Previous match"
          className={`flex shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors ${theme.buttonGhost} border ${theme.border} disabled:opacity-40`}
        >
          <ChevronLeft size={15} />
        </button>
        <span className={`min-w-0 flex-1 text-center text-xs font-medium ${theme.textMuted}`}>{currentLabel}</span>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          aria-label="Next match"
          className={`flex shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors ${theme.buttonGhost} border ${theme.border} disabled:opacity-40`}
        >
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <MatchDetailPanel
          row={row}
          tournamentVO={tournamentVO}
          currentUserId={currentUserId}
          onStartScoring={handleStartScoring}
          onOpenLive={handleOpenLive}
          onBack={onClose}
        />
      </div>
    </div>,
    document.body
  );
};

export default MatchDetailModal;
